import { useEffect } from "react";
import { useDropzone } from "react-dropzone";
import Resizer from "react-image-file-resizer";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import UploadButton from "./UploadButton";
import PreviewThumb from "./PreviewThumb";
import AppLabel from "../AppLabel";
import { isEmptyFile } from "../../../utils/validators";

interface UploadSingleProps {
  preview: any;
  initialUrl?: string;
  setUploadedFile: (uri: any) => void;
  uploadFile?: File | null;
  setPreview: (uri: any) => void;
  label?: string;
  uploadText: string;
  handleSubmit?: () => void;
  uploading?: boolean;
}

const AppUploadSingle: React.FC<UploadSingleProps> = ({
  setUploadedFile,
  setPreview,
  preview,
  initialUrl,
  label,
  uploadText,
  handleSubmit,
  uploadFile,
  uploading = false,
}) => {
  const { t } = useTranslation();
  const dropzone = useDropzone({
    accept: {
      "image/png": [".png"],
      "image/jpg": [".jpg"],
      "image/jpeg": [".jpeg"],
    },
    onDrop: (acceptedFiles) => {
      const file: File = acceptedFiles[0];
      setPreview(window.URL.createObjectURL(file));
      Resizer.imageFileResizer(file, 720, 500, "JPEG", 100, 0, (uri) => {
        setUploadedFile(uri);
      });
    },
  });

  useEffect(() => {
    setUploadedFile(dropzone.acceptedFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropzone.acceptedFiles]);

  const onDeleteUploadFile = (file: any) => {
    setUploadedFile(null);
    setPreview(null);
  };
  const isEmpty = isEmptyFile(uploadFile);

  return (
    <>
      <AppLabel>{label}</AppLabel>
      <section className="container" style={{ cursor: "pointer" }}>
        <UploadButton uploadText={uploadText} dropzone={dropzone} />
        {(preview || initialUrl) && (
          <PreviewThumb
            preview={preview ?? initialUrl}
            onDeleteUploadFile={onDeleteUploadFile}
          />
        )}
        {handleSubmit && !isEmpty && (
          <Button
            variant="contained"
            color="warning"
            fullWidth
            sx={{ textTransform: "none", p: 2 }}
            onClick={handleSubmit}
            disabled={uploading}
          >
            {uploading ? <CircularProgress color="inherit" /> : t("saveChange")}
          </Button>
        )}
      </section>
    </>
  );
};

export default AppUploadSingle;
